import React, { useCallback, useEffect, useReducer } from "react"
// Admin App - Main Application Entry Point
import "react-toastify/dist/ReactToastify.css"
import "mapbox-gl/dist/mapbox-gl.css"

import "./index.css"
import "@ciq/shared/css/App.css"
import "@ciq/shared/css/AttributeInput.css"
import "@ciq/shared/css/Button.css"
import "@ciq/shared/css/LoginRedirect.css"
import "@ciq/shared/css/NavBar.css"
import "@ciq/shared/css/SplitLayout.css"
import "@ciq/shared/css/TableView.css"
import "@ciq/shared/css/Components.css"

import "@ciq/shared/css/pages/DashboardPage.css"
import "@ciq/shared/css/pages/MapPage.css"
import "@ciq/shared/css/pages/SchoolPage.css"
import "@ciq/shared/css/pages/SettingsPage.css"
import "@ciq/shared/css/pages/WirelessConfigPage.css"

import "./css/App.css"
import "./css/NavBar.css"
import "./css/SchoolPage.css"
import "./css/AlertsPage.css"
import "./css/SparkPage.css"
import "./css/SettingsPage.css"

import { BrowserRouter, Outlet, Route, Routes, useNavigate } from "react-router-dom"
import { useLocation } from "react-router"
import { ToastContainer } from "react-toastify"

import Api from "@ciq/shared/api/api"
import eventBus from "@ciq/shared/hooks/eventBus"

import NavigationBar from "@ciq/shared/components/navbar"
import IdleTimeout from "@ciq/shared/components/idle_timeout"
import BoxedMarker from "@ciq/shared/components/boxed_marker"

import MapView from "./pages/map/view"
import AdminConsole from "./pages/admin_console"
import SchoolView from "./pages/school/view"
import WirelessConfigView from "./pages/wireless_config/view"
import AlertsView from "./pages/alerts/view"

import { ReactComponent as AlertIcon } from "@ciq/shared/assets/icons/navbar/alert_icon.svg"
import { ReactComponent as DashboardIcon } from "@ciq/shared/assets/icons/navbar/dashboard_icon.svg"
import { ReactComponent as LogoutIcon } from "@ciq/shared/assets/icons/navbar/logout_icon.svg"
import { ReactComponent as MapIcon } from "@ciq/shared/assets/icons/navbar/map_icon.svg"
import { ReactComponent as SchoolIcon } from "@ciq/shared/assets/icons/navbar/school_icon.svg"
import { ReactComponent as SettingsIcon } from "@ciq/shared/assets/icons/navbar/settings_icon.svg"
import { ReactComponent as WirelessConfigIcon } from "@ciq/shared/assets/icons/navbar/wireless_icon.svg"

import SettingsModal from "./components/modals/settings"
import { ErrorToast } from "@ciq/shared/utils/toast"
import { initMap } from "@ciq/shared/hooks/map"
import { SchoolDropdown } from "./components/dropdown"
import { AlertDetail } from "./pages/alerts/detail"
import { TabOptions } from "./pages/wireless_config/consts"
import { SparkView } from "./pages/spark/view"

import { AlertContext, useAlertProvider } from "./hooks/alert_context"
import { AppProvider, useAppContext } from "@ciq/shared/hooks/context"
import { GlobalIndicator } from "@ciq/shared/components/global_indicator"
import { Landing } from "@ciq/shared/components/landing"
import { Console } from "@ciq/shared/components/console"
import { ErrorMessage } from "@ciq/shared/components/error"
import { getCurrentId, NAVIGATION_OPTION_IDS } from "./utils/getCurrentId"
import { CookieConsentProvider } from "@ciq/shared/contexts/CookieConsentContext"
import { AnalyticsProvider } from "@ciq/shared/contexts/AnalyticsContext"
import "@ciq/shared/components/cookie_banner/CookieBanner.css"

initMap()

const DEFAULT_DATA = {
  loading: false,
  showSettings: false,
  wifiConfigDataReload: {
    state: 0,
  },
}

const SET_LOADING = "SET_LOADING"
const SHOW_SETTINGS = "SHOW_SETTINGS"

const SET_NOTIFICATIONS = "SET_NOTIFICATIONS"
const SET_USER_SETTINGS = "SET_USER_SETTINGS"

const SET_WIFI_CONFIG_DATA_RELOAD = "SET_WIFI_CONFIG_DATA_RELOAD"

const AppReducer = (state, action) => {
  switch (action.type) {
    case SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      }

    case SHOW_SETTINGS:
      return {
        ...state,
        showSettings: action.payload,
      }

    case SET_NOTIFICATIONS:
      return {
        ...state,
        ...action.payload,
      }

    case SET_USER_SETTINGS:
      return {
        ...state,
        ...action.payload,
      }

    case SET_WIFI_CONFIG_DATA_RELOAD: {
      return {
        ...state,
        wifiConfigDataReload: {
          state: action.payload,
        },
      }
    }

    default:
      throw new Error(`Unknown action type ${action.type}`)
  }
}

const NON_LOGIN_SCREEN_PATHS = ["/error"]

function App() {
  const onLogout = useCallback(() => {
    Api.post(`/auth/logout`, {})
      .catch(e => {
        if (e.handled) return

        console.error(e.error)
        ErrorToast("Unable to logout")
      })
      .finally(() => {
        eventBus.emit("logout")
      })
  }, [])

  return (
    <AppProvider>
      <CookieConsentProvider>
        <AnalyticsProvider>
          <IdleTimeout onLogout={onLogout} />
          <div id="map-content" />
          <Landing noUserRequiredPaths={NON_LOGIN_SCREEN_PATHS} />
          <View />
          <ToastContainer position="bottom-center" />
          <GlobalIndicator />
        </AnalyticsProvider>
      </CookieConsentProvider>
    </AppProvider>
  )
}

function View() {
  const {
    isSystemAdmin,
    school,
    schools,
    setSchool,
    notifications,
    setNotifications,
    hasData,
    setShowSettings,
  } = useAppContext()
  const [state, dispatch] = useReducer(AppReducer, DEFAULT_DATA)
  const isError = window.location.pathname === "/error"

  const flagWifiConfigDataReload = () => dispatch({ type: SET_WIFI_CONFIG_DATA_RELOAD, payload: 1 })

  const clearWifiConfigDataReload = useCallback(() => {
    dispatch({ type: SET_WIFI_CONFIG_DATA_RELOAD, payload: 0 })
  }, [])

  const onDropdownItemSelected = useCallback(
    (optionId, navigate) => {
      switch (optionId) {
        case DROPDOWN_OPTION_IDS.HELP:
          console.debug("HELP clicked")
          break

        case DROPDOWN_OPTION_IDS.CHAT:
          console.debug("CHAT clicked")
          break

        case DROPDOWN_OPTION_IDS.SETTINGS:
          setShowSettings(true)
          break

        case DROPDOWN_OPTION_IDS.ADMIN_DASHBOARD:
          navigate(NAVIGATION_OPTION_IDS.ADMIN_DASHBOARD.getPath())
          break

        case DROPDOWN_OPTION_IDS.ALERT:
          navigate(NAVIGATION_OPTION_IDS.ALERT.getPath())
          break

        case DROPDOWN_OPTION_IDS.SPARK:
          navigate(NAVIGATION_OPTION_IDS.SPARK.getPath())
          break

        case DROPDOWN_OPTION_IDS.LOGOUT:
          Api.post(`/auth/logout`, {})
            .catch(e => {
              if (e.handled) return

              console.error(e.error)
              ErrorToast("Unable to logout")
            })
            .finally(() => {
              eventBus.emit("logout")
            })
          break

        default:
          throw new Error(`Unknown DropdownItem id ${optionId}`)
      }
    },
    [setShowSettings]
  )

  const handleOnPreviousSchool = useCallback(() => {
    if (school?.id == null) {
      return 0
    }

    let newIndex = schools.findIndex(s => s.id === school?.id) - 1
    if (newIndex < 0) {
      newIndex = schools.length - 1
    }

    setSchool(schools[newIndex])
  }, [school?.id, schools, setSchool])

  const handleOnNextSchool = useCallback(() => {
    if (school?.id == null) {
      return 0
    }

    let newIndex = schools.findIndex(s => s.id === school?.id) + 1
    if (newIndex >= schools.length) {
      newIndex = 0
    }

    setSchool(schools[newIndex])
  }, [school?.id, schools, setSchool])

  if (isError) {
    return (
      <>
        <BrowserRouter>
          <Routes>
            <Route path="/error" element={<ErrorMessage />} />
          </Routes>
        </BrowserRouter>
      </>
    )
  }

  if (!hasData) {
    return null
  }

  return (
    <>
      <BrowserRouter>
        <Routes>
          <Route path="/error" element={<ErrorMessage />} />
          <Route path="/" element={<Layout onDropdownItemSelected={onDropdownItemSelected} />}>
            <Route path="/" element={<InitialView />} />
            {isSystemAdmin && (
              <Route path={NAVIGATION_OPTION_IDS.MAP.getPath()} element={<MapView />} />
            )}
            {isSystemAdmin && (
              <Route
                path={NAVIGATION_OPTION_IDS.ADMIN_DASHBOARD.getPath()}
                element={<AdminConsole />}
              />
            )}
            {isSystemAdmin && (
              <Route path={NAVIGATION_OPTION_IDS.DASHBOARD.getPath()} element={<Console />} />
            )}
            {isSystemAdmin && (
              <Route
                path={NAVIGATION_OPTION_IDS.SCHOOL.getPath()}
                element={
                  <SchoolView
                    carouselDisabled={schools == null || schools.length <= 1}
                    onPreviousSchool={handleOnPreviousSchool}
                    onNextSchool={handleOnNextSchool}
                  />
                }
              />
            )}
            <Route
              path={NAVIGATION_OPTION_IDS.WIRELESS_CONFIG.getPath()}
              element={
                <WirelessConfigView
                  school={school}
                  reloadState={state.wifiConfigDataReload.state}
                  onReloaded={clearWifiConfigDataReload}
                />
              }
            />
            {isSystemAdmin && <Route path="/alert" element={<AlertsView />} />}
            {isSystemAdmin && <Route path="/alert/:alertId" element={<AlertDetail />} />}
            {isSystemAdmin && <Route path="/spark" element={<SparkView school={school} />} />}
          </Route>
        </Routes>
      </BrowserRouter>
      <div id="modal-content" />
      {isSystemAdmin && (
        <SettingsModal
          notifications={notifications.settings}
          onNotificationsUpdated={() => {
            Api.get(`/landing/notifications`)
              .then(response => setNotifications(response.data))
              .catch(e => {
                if (e.handled) return
                console.error(e.error)
                ErrorToast("Unable to fetch notifications")
              })
          }}
          onApsUpdated={flagWifiConfigDataReload}
        />
      )}
    </>
  )
}

const InitialView = () => {
  const { isSystemAdmin } = useAppContext()
  const location = useLocation()
  const navigate = useNavigate()

  const { pathname } = location

  useEffect(() => {
    if (pathname !== "/") {
      return
    }

    navigate(getInitialRoute(isSystemAdmin), { replace: true })
  }, [isSystemAdmin, navigate, pathname])

  return null
}

const NAVIGATION_OPTIONS = {
  badgeCount: 0,
  routes: [
    {
      id: NAVIGATION_OPTION_IDS.MAP.id,
      requiresSystemAdmin: true,
      label: "Map",
      icon: <MapIcon />,
      rightElement: <BoxedMarker text="BETA" />,
    },
    {
      id: NAVIGATION_OPTION_IDS.DASHBOARD.id,
      requiresSystemAdmin: true,
      label: "Dashboard",
      icon: <DashboardIcon />,
    },
    {
      id: NAVIGATION_OPTION_IDS.SCHOOL.id,
      requiresSystemAdmin: true,
      label: "School",
      icon: <SchoolIcon />,
    },
    {
      id: NAVIGATION_OPTION_IDS.WIRELESS_CONFIG.id,
      requiresSystemAdmin: false,
      label: "Wireless Config",
      search: `?tab=${TabOptions[0].id}`,
      icon: <WirelessConfigIcon />,
    },
  ],
}

const getInitialRoute = isSystemAdmin => {
  if (isSystemAdmin) {
    return NAVIGATION_OPTION_IDS.SCHOOL.getPath()
  }

  return NAVIGATION_OPTION_IDS.WIRELESS_CONFIG.getPath()
}

const DROPDOWN_OPTION_IDS = {
  HELP: "help",
  CHAT: "chat",
  SETTINGS: "settings",
  ADMIN_DASHBOARD: "adminDashboard",
  ALERT: "alert",
  SPARK: "spark",
  LOGOUT: "logout",
}

const DROPDOWN_OPTIONS = [
  // [
  //   {id: DROPDOWN_OPTION_IDS.HELP, label: "Help & Tutorials", icon: <HelpIcon/>},
  //   {id: DROPDOWN_OPTION_IDS.CHAT, label: "Chat with Support", icon: <ChatIcon/>}
  // ],
  [
    {
      id: DROPDOWN_OPTION_IDS.SETTINGS,
      requiresSystemAdmin: true,
      label: "Settings",
      icon: <SettingsIcon />,
    },
    {
      id: DROPDOWN_OPTION_IDS.ADMIN_DASHBOARD,
      requiresSystemAdmin: true,
      label: "Admin Dashboard",
      icon: <DashboardIcon />,
    },
    {
      id: DROPDOWN_OPTION_IDS.ALERT,
      requiresSystemAdmin: true,
      label: "Alerts",
      icon: <AlertIcon />,
    },
    {
      id: DROPDOWN_OPTION_IDS.SPARK,
      requiresSystemAdmin: true,
      label: "Spark",
      icon: <AlertIcon />,
    },
    {
      id: DROPDOWN_OPTION_IDS.LOGOUT,
      requiresSystemAdmin: false,
      label: "Log out",
      className: "danger-action",
      icon: <LogoutIcon />,
    },
  ],
]

function Layout({ onDropdownItemSelected }) {
  const { isSystemAdmin, notifications } = useAppContext()
  const alertProvider = useAlertProvider()
  const location = useLocation()
  const navigate = useNavigate()
  const currentId = getCurrentId(isSystemAdmin, location)

  const permissionFilter = o => !o.requiresSystemAdmin || (o.requiresSystemAdmin && isSystemAdmin)

  const navigationOptions = {
    ...{
      ...NAVIGATION_OPTIONS,
      routes: NAVIGATION_OPTIONS.routes.filter(permissionFilter),
    },
    badgeCount: notifications.count,
  }

  const buildOption = option => {
    return {
      ...option,
      badgeCount: notifications[option.id] != null ? notifications[option.id].count : 0,
    }
  }

  const dropdownOptions = DROPDOWN_OPTIONS.map(dropdown =>
    dropdown.filter(permissionFilter).map(buildOption)
  )

  const handleDropdownItemSelected = optionId => {
    onDropdownItemSelected(optionId, navigate)
  }

  return (
    <AlertContext.Provider value={alertProvider}>
      <div className="app-container">
        <NavigationBar
          currentId={currentId}
          navigationOptions={navigationOptions}
          dropdownOptions={dropdownOptions}
          onDropdownItemSelected={handleDropdownItemSelected}
        >
          <SchoolDropdown />
        </NavigationBar>
        <Outlet />
      </div>
    </AlertContext.Provider>
  )
}

export default App
